import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import { useFileMetadata } from '@/shared/hooks';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { downloadBlob, getFileDisplayName } from '@/shared/utils';
import { checkIsNotFoundError } from '@/shared/utils/error-handler';
import type { TResolvedValue } from '../types';
import { getDocumentFileUrls } from '../utils/idp-document-location';
import { fetchRemoteFile } from './useFileDownload';
import {
  getIDPDocumentName,
  isIDPBundle,
  isIDPBundleList,
  isIDPList,
  isIDPOutput,
} from '../utils';

/** The source document behind an IDP output. */
export interface IIDPDocumentName {
  /** Resource name of the source document, if the output names one */
  documentName: string | undefined;
  /** The document's real file name once its metadata loads, else a fallback */
  displayName: string;
  /** Saves the source document under its display name */
  download: () => Promise<void>;
}

/**
 * The source document of an IDP output: its uploaded file name and a download
 * of the file itself, shared by the cards and the output modal.
 *
 * @param value - Any resolved output value
 * @returns The document, or null when the value is not an IDP output
 */
export function useIDPDocumentName(
  value: TResolvedValue,
): IIDPDocumentName | null {
  const { t } = useTranslation('outputs');
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const isIDP =
    isIDPOutput(value) ||
    isIDPList(value) ||
    isIDPBundle(value) ||
    isIDPBundleList(value);
  const documentName = isIDP ? getIDPDocumentName(value) : undefined;
  const { data: metadata } = useFileMetadata(
    organization && documentName
      ? {
          organization,
          file: documentName,
          ...(workspace ? { workspace } : {}),
        }
      : null,
  );

  const filename = metadata?.data?.filename;
  const displayName = filename
    ? getFileDisplayName(filename)
    : documentName
      ? getFileDisplayName(documentName)
      : t('outputs.idp.modalTitle');

  const download = useCallback(async () => {
    const locations = getDocumentFileUrls(
      documentName ?? '',
      organization,
      workspace,
    );
    try {
      for (const [index, location] of locations.entries()) {
        try {
          const file = await fetchRemoteFile(location);
          downloadBlob(file.blob, displayName);
          return;
        } catch (error) {
          // Same rule as the thumbnail: only a missing file moves on.
          const isLast = index === locations.length - 1;
          if (isLast || !checkIsNotFoundError(error)) throw error;
        }
      }
      toast.error(t('outputs.error.failedToDownloadFile'));
    } catch (error) {
      toast.error(t('outputs.error.failedToDownloadFile'));
      captureException(error, {
        tags: { module: 'outputs', action: 'downloadIDPDocument' },
      });
    }
  }, [displayName, documentName, organization, t, workspace]);

  if (!isIDP) return null;
  return { documentName, displayName, download };
}
